import type { ReactNode } from 'react';
import { ReloadOutlined, SearchOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Button, FilterOptionList, FilterPopover, Input, Segmented } from '@/components/ui';
import styles from './AccountsToolbar.module.css';

/** EAA-1839 (frame 35 toolbar): the "Cột" menu — one entry per hideable column. */
export interface AccountsColumnToggle {
    key: string;
    label: string;
    visible: boolean;
    /** Present ⇒ the entry is inert with this as the reason (e.g. a column the row shape always needs). */
    lockedReason?: string;
}

export type AccountsDensity = 'comfortable' | 'compact';

/**
 * EAA-1839 (2026-08-21, user request): where the facet rail renders —
 * `'sidebar'` is frame 35's only drawn layout (the 236px rail column);
 * `'header'` is the ADDITION the user asked for so the table can reclaim that
 * width, collapsing the six facet groups behind a "Bộ lọc (N)" trigger above
 * the table instead. See `AccountsScreen.tsx` for the layout switch and
 * `usePersistedAccountsViewConfig` for why this is stored alongside density/columns.
 */
export type AccountsPlacement = 'sidebar' | 'header';

export interface AccountsToolbarProps {
    search: string;
    onSearchChange: (value: string) => void;
    /** Present (with a reason) when this context's backend has no `search` param —
     *  see the org adapter: `OrganizationAccountsView` takes no `search` at all. */
    searchDisabledReason?: string;
    /** EAA-1839: override the shared placeholder — the org context searches only
     *  `user_email`/`user_sub`, not name/username, and the shared copy over-promises
     *  for it. Omit to keep the shared `console.accounts.filters.searchPlaceholder`. */
    searchPlaceholder?: string;
    /** Same reasoning as `searchPlaceholder`, for the input's accessible name. */
    searchAriaLabel?: string;
    activeFilterCount: number;
    onClearFilters: () => void;
    /* EAA-1839: there is deliberately NO range readout ("Hiển thị 1–50 / N")
     * here. The table footer already states that range directly under the rows
     * it describes, so a second copy spent toolbar width — a row that has been
     * at capacity for three passes — on a duplicate. The prop that used to
     * carry it (`rangeLabel`) was kept optional for one round so the three
     * boards compiled, and is now removed from here and from all of them.
     * Do not reintroduce it: if a count belongs in the toolbar it must be a
     * fact the footer does NOT already give. */
    /** EAA-1839: an honest caveat rendered on its own row under the toolbar's
     *  controls (e.g. "the client-side search walk hit its cap; results may be
     *  incomplete"). Omit ⇒ nothing renders. Deliberately its own row rather than
     *  folded into any range/count readout — the two are different kinds of fact
     *  (a range vs. a warning) and concatenating them buries the warning half the
     *  time. */
    searchCapNote?: string;

    /**
     * EAA-1839 (2026-08-21, user request): the "Bộ lọc (N)" trigger that replaces
     * the sidebar rail when `placement` is `'header'` — rendered directly after
     * the search input, before the active-filter chip and the spacer. This used
     * to be `AccountsScreen`'s own `.acc-filterbar` row ABOVE this toolbar; the
     * user asked for it in the same row as search instead. Optional and
     * caller-owned (the caller builds the `FilterPopover` + `Button` and hands it
     * through) so the realm screen, which never passes it, renders unchanged.
     */
    filterTrigger?: ReactNode;
    /**
     * EAA-1839: caller-owned actions, rendered after the spacer beside "Hiển thị".
     *
     * Added because the App Detail accounts tab has a real action of its own ("Lấy
     * người dùng từ LMS") and `filterTrigger` was the only `ReactNode` this toolbar
     * accepted — so that button had to ride along inside the filter slot, which put
     * an action where a filter belongs and left it on the wrong side of the spacer.
     * A slot with a name beats a slot borrowed for the wrong purpose.
     */
    actions?: ReactNode;

    /** EAA-1839 (frame 35): "Cột" — column visibility. Omitted ⇒ no button. */
    columns?: { items: AccountsColumnToggle[]; onToggle: (key: string) => void };
    /** EAA-1839 (frame 35): "Mật độ" — row height. Omitted ⇒ no control. */
    density?: { value: AccountsDensity; onChange: (value: AccountsDensity) => void };
    /**
     * EAA-1839 (2026-08-21, user request): "Vị trí bộ lọc" — a THIRD section in
     * the same "Hiển thị" popover as `columns`/`density`, not a fourth top-level
     * toolbar control. The toolbar row is already search + chip + this popover +
     * refresh + range text, wrapping on narrow widths — a rail-placement toggle
     * is exactly the kind of low-frequency display preference `density` already
     * models, so it earns a `Segmented` beside it rather than its own button.
     * Omitted ⇒ no section (and `AccountsScreen` defaults to `'sidebar'`).
     */
    placement?: { value: AccountsPlacement; onChange: (value: AccountsPlacement) => void };
    /** EAA-1839 (frame 35): the refresh glyph. Omitted ⇒ no button. */
    onRefresh?: () => void;
    refreshBusy?: boolean;
}

/**
 * EAA-1820: the ONE board toolbar (search box, active-filter chip, range
 * readout) both screens render — replaces `accounts/AccountsFilters.tsx`. The
 * org screen's search box used to be its own inert, disabled-styled markup
 * (`ScopedAccountsHeader`'s `sa-search`); it renders through this same component
 * now, just with `searchDisabledReason` set, so "search does not work here" looks
 * and behaves identically to every other honestly-disabled control in this app
 * rather than being a one-off.
 *
 * EAA-1839 (2026-08-21, user request): the org screen's search box went from
 * permanently disabled to a real client-side walk-and-filter
 * (`useScopedAccounts`'s `searchWalkQuery`/`searchMatches`) over `user_email` and
 * `user_sub` — the only two fields the row carries. `searchDisabledReason` stays
 * on the props (a future backend gap could still need it; the realm screen may
 * never), but the org page no longer passes it.
 *
 * EAA-1839 (frame 35 completion pass): frame 35's toolbar carries three more
 * controls to the right of the range readout — "Cột", "Mật độ" and a refresh
 * glyph. All three are OPTIONAL props rather than always-rendered chrome, because
 * each is only real where the caller can back it: column visibility needs a column
 * set the page owns, density needs a table class to switch, and refresh needs a
 * query to invalidate. A context that has none passes none and the toolbar renders
 * exactly as it did before this pass.
 *
 * EAA-1839 (2026-08-21, user-requested merge, TWO revisions): frame 35 draws
 * "Cột" and "Mật độ" as two SEPARATE controls. The user asked for them folded
 * into one "Hiển thị" control, so a later pass comparing this screen against the
 * design file should NOT "fix" it apart — this is a deliberate deviation. The
 * merge went through two shapes:
 *  1. A `DropdownMenu` whose items mixed an imperative verb ("Ẩn "Đơn vị"") with
 *     state (a check glyph + "— Đang dùng" for density) — flagged by the user as
 *     reading inconsistently, since you had to read a verb to infer a state that
 *     a checkbox would just show.
 *  2. THIS one: a `FilterPopover` (packages/ui/ColumnFilter) holding a
 *     `FilterOptionList` of plain `Checkbox` rows for columns (checked = visible,
 *     no verb) and a `Segmented` for density (shows its own current value, no
 *     glyph/suffix needed). `FilterPopover`, NOT the antd `Dropdown`-backed
 *     `DropdownMenu`: a `DropdownMenu` item closes the menu on select, so hiding
 *     three columns cost three open→click→close cycles — `FilterPopover` stays
 *     open across every toggle, which is the entire reason for the swap and not
 *     a style preference. Do not revert to a closing menu.
 * `columns`/`density` are unchanged (still two independent optional objects);
 * only the RENDERING changed, twice.
 */
export function AccountsToolbar({
    search,
    onSearchChange,
    searchDisabledReason,
    searchPlaceholder,
    searchAriaLabel,
    activeFilterCount,
    onClearFilters,
    searchCapNote,
    filterTrigger,
    actions,
    columns,
    density,
    placement,
    onRefresh,
    refreshBusy,
}: AccountsToolbarProps) {
    const { t } = useTranslation();

    const visibleColumnKeys = columns
        ? columns.items.filter((item) => item.visible).map((item) => item.key)
        : [];

    const handleColumnsChange = (next: string[]) => {
        if (!columns) return;
        const nextSet = new Set(next);
        for (const item of columns.items) {
            // EAA-1839: `FilterOptionList` hands back the whole next selection, not
            // a single toggled key, and `columns.onToggle` flips exactly one key —
            // so only forward the keys whose membership actually changed. A locked
            // item's checked state never changes here (its key stays out of
            // `nextSet` exactly when it was already out), so it silently ignores a
            // click rather than needing its own disabled affordance.
            const wasVisible = item.visible;
            const isVisible = nextSet.has(item.key);
            if (wasVisible !== isVisible && !item.lockedReason) columns.onToggle(item.key);
        }
    };

    return (
        <div className="acc-toolbar">
            <Input
                className={styles.search}
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder ?? t('console.accounts.filters.searchPlaceholder')}
                aria-label={searchAriaLabel ?? t('console.accounts.filters.searchAriaLabel')}
                prefix={<SearchOutlined />}
                allowClear
                disabled={Boolean(searchDisabledReason)}
                title={searchDisabledReason}
            />

            {filterTrigger}

            {activeFilterCount > 0 && (
                <Button className={styles.chip} onClick={onClearFilters}>
                    {t('console.accounts.filters.activeFilterChip', { count: activeFilterCount })}
                </Button>
            )}

            <span className={styles.spacer} />

            {actions}

            {(columns || density || placement) && (
                <FilterPopover
                    align="end"
                    width={220}
                    panelClassName="acc-toolbar__display-panel"
                    trigger={({ open, ref, toggle }) => (
                        <Button
                            ref={ref}
                            className={styles.display}
                            icon={<SettingOutlined />}
                            aria-label={t('console.accounts.filters.displayAriaLabel')}
                            aria-expanded={open}
                            onClick={toggle}
                        >
                            {t('console.accounts.filters.displayButton')}
                        </Button>
                    )}
                >
                    {columns && (
                        <div className={styles.displayGroup}>
                            <p className={styles.displayHeading}>
                                {t('console.accounts.filters.columnsHeading')}
                            </p>
                            <FilterOptionList
                                label={t('console.accounts.filters.columnsHeading')}
                                options={columns.items.map((item) => ({
                                    value: item.key,
                                    label: item.label,
                                }))}
                                value={visibleColumnKeys}
                                onChange={handleColumnsChange}
                            />
                        </div>
                    )}
                    {density && (
                        <div className={styles.displayGroup}>
                            <p className={styles.displayHeading}>
                                {t('console.accounts.filters.densityHeading')}
                            </p>
                            <Segmented
                                className={styles.displayDensity}
                                size="small"
                                value={density.value}
                                onChange={(value) => density.onChange(value as AccountsDensity)}
                                options={[
                                    {
                                        value: 'comfortable',
                                        label: t('console.accounts.filters.densityComfortable'),
                                    },
                                    { value: 'compact', label: t('console.accounts.filters.densityCompact') },
                                ]}
                                aria-label={t('console.accounts.filters.densityHeading')}
                            />
                        </div>
                    )}
                    {placement && (
                        <div className={styles.displayGroup}>
                            <p className={styles.displayHeading}>
                                {t('console.accounts.filters.placementHeading')}
                            </p>
                            <Segmented
                                className={styles.displayDensity}
                                size="small"
                                value={placement.value}
                                onChange={(value) => placement.onChange(value as AccountsPlacement)}
                                options={[
                                    {
                                        value: 'sidebar',
                                        label: t('console.accounts.filters.placementSidebar'),
                                    },
                                    {
                                        value: 'header',
                                        label: t('console.accounts.filters.placementHeader'),
                                    },
                                ]}
                                aria-label={t('console.accounts.filters.placementHeading')}
                            />
                        </div>
                    )}
                </FilterPopover>
            )}

            {onRefresh && (
                <Button
                    className={styles.refresh}
                    icon={<ReloadOutlined />}
                    onClick={onRefresh}
                    loading={refreshBusy}
                    aria-label={t('console.accounts.filters.refreshAriaLabel')}
                    title={t('console.accounts.filters.refreshAriaLabel')}
                />
            )}

            {searchCapNote && <p className={styles.capNote}>{searchCapNote}</p>}
        </div>
    );
}

export default AccountsToolbar;
